<template>
  <v-container fluid class="ma-4 mt-3 px-8">
    <v-row justify="start" align="center">
      <v-col cols="12" md="2" class="py-0 my-0">
        <v-autocomplete
          v-model="form.types"
          :items="feeTypes"
          outlined
          dense
          hide-details="auto"
          clearable
          label="Particular"
          item-text="text"
          item-value="id"
          multiple
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="2" class="py-0 my-0">
        <v-select
          v-model="form.raised_batch"
          :items="feeRaisedBatches"
          outlined
          dense
          hide-details="auto"
          clearable
          label="Fee Raised Batch"
        ></v-select>
      </v-col>
      <v-col cols="12" md="2" class="py-0 my-0">
        <v-select
          v-model="form.year_batch"
          :items="batches"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Batch"
        ></v-select>
      </v-col>
      <v-col cols="12" md="2" class="py-0 my-0">
        <v-autocomplete
          v-model="form.program_id"
          :items="programs"
          outlined
          dense
          clearable
          hide-details="auto"
          :item-text="
            (item) =>
              `${item.degree.short_name} ${
                item.specialization ? `- ${item.specialization}` : ''
              }`
          "
          item-value="id"
          label="Programme"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="2" class="py-0 my-0">
        <v-autocomplete
          v-model="form.partner_id"
          :items="partners"
          outlined
          dense
          clearable
          hide-details="auto"
          item-text="name"
          item-value="id"
          label="Partner"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="2" class="py-0 my-0">
        <v-autocomplete
          v-model="form.status"
          :items="statuses"
          item-text="name"
          item-value="id"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Status"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="2" class="py-0 my-0">
        <v-text-field
          v-model.trim="form.usn_id"
          label="USN"
          outlined
          dense
          hide-details="auto"
          clearable
        />
      </v-col>
      <v-col cols="12" md="2" class="d-flex justify-start">
        <v-btn
          class="accent white--text"
          :loading="loading"
          @click="init()"
          :disabled="loading"
        >
          Search
        </v-btn>
        <v-btn
          :disabled="!studentInvoices.length"
          :loading="downloadLoader"
          class="accent white--text ml-2"
          @click="download()"
        >
          <v-icon>mdi-download</v-icon> Download
        </v-btn>
        <v-btn
          class="accent white--text ml-2"
          @click="showConfigureDueDialog()"
          :disabled="
            (!selectedApplicants.length && form.status != 'Pending') ||
            !form.raised_batch ||
            !form.types ||
            !form?.types.length
          "
        >
          Extend Due Date
        </v-btn>
        <v-btn
          :loading="historicalDownloadLoader"
          class="accent white--text ml-2"
          @click="historicalFeeDownload()"
        >
          <v-icon>mdi-download</v-icon> Historical Fee
        </v-btn>
        <v-btn
          v-if="sendEmail && form.status == 'Incomplete'"
          small
          :disabled="!selectedApplicants.length"
          fab
          class="accent white--text ml-2"
          @click="emailTrigger = true"
        >
          <v-icon small>mdi-email</v-icon>
        </v-btn>
        <v-btn
          v-if="
            form.status === 'Pending' &&
            form?.types?.length === 1 &&
            form.types[0] === 'Exam Fee'
          "
          class="accent white--text ml-2"
          :disabled="!selectedApplicants.length"
          :loading="markInactiveLoader"
          @click="confirmMarkInactive = true"
        >
          <v-icon small left>mdi-account-off</v-icon>
          Mark Inactive
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            v-model="selectedApplicants"
            :headers="headers"
            :items="studentInvoices"
            fixed-header
            item-key="invoice_id"
            :show-select="sendEmail || isDueFilter"
            :show-select-all="sendEmail || isDueFilter"
            :multi-sort="true"
            return-object
            selectable-key="activeSelectable"
          >
            <template v-slot:no-data>
              <div class="text-center">No data available</div>
            </template>
            <template #[`item.papers`]="{ item }">
              <v-icon
                v-if="item.type == 'Exam Fee'"
                small
                class="mr-2"
                color="accent"
                @click="listPapers(item)"
              >
                mdi-dots-horizontal
              </v-icon>
              <div v-else>-</div>
            </template>
            <template #[`item.action`]="{ item }">
              <v-icon
                small
                class="mr-2"
                color="accent"
                @click="edit(item)"
                :disabled="item.status == 'Paid'"
              >
                mdi-pencil
              </v-icon>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>
    <v-dialog v-model="examPaper" max-width="600px">
      <examPaperDialog
        v-if="examPaper"
        @closeDialog="(examPaper = false), (studentExamPapers = [])"
        :studentExamPapers="studentExamPapers"
      />
    </v-dialog>
    <v-dialog v-model="emailTrigger" max-width="800">
      <emailTriggerDialog
        v-if="emailTrigger"
        @closeDialog="(emailTrigger = false), (selectedApplicants = [])"
        @trigger="triggerPendingApplicantEmail"
        :selectedApplicants="selectedApplicants"
      />
    </v-dialog>
    <v-dialog v-model="amountChange" width="800" persistent scrollable>
      <amountChangeDialog
        v-if="amountChange"
        @closeDialog="amountChange = false"
        @save="(amountChange = false), init()"
        :invoice="academicPayment"
        :options_to_display="options_to_display"
      />
    </v-dialog>
    <v-dialog v-model="configureDueDateDialog" max-width="600">
      <configureDueDateDialog
        v-if="configureDueDateDialog"
        @closeDialog="configureDueDateDialog = false"
        @configure="configureDueDate"
        :selectedApplicants="studentInvoices"
        :selectedCount="studentInvoices.length"
        :feeTypes="form.types"
        :raisedBatch="form.raised_batch"
      />
    </v-dialog>

    <!-- Mark Inactive Confirmation Dialog -->
    <v-dialog v-model="confirmMarkInactive" max-width="480" persistent>
      <v-card class="confirm-dialog">
        <div class="confirm-dialog__header confirm-dialog__header--inactive">
          <v-icon color="white" size="32" class="mb-2">mdi-account-off</v-icon>
          <div class="confirm-dialog__title">Confirm Mark Inactive</div>
          <div class="confirm-dialog__sub">This action cannot be undone</div>
        </div>
        <v-card-text class="pt-4">
          <div class="confirm-info mb-3">
            You are about to update
            <strong>{{ selectedApplicants.length }}</strong> student(s):
          </div>

          <v-list dense class="confirm-list">
            <v-list-item
              v-for="s in showAllInactive
                ? selectedApplicants
                : selectedApplicants.slice(0, 5)"
              :key="s.usn_id"
              class="px-0"
            >
              <v-list-item-icon class="mr-2 my-2">
                <v-icon small color="indigo">mdi-account</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="body-2">{{
                  s.student_name || s.name
                }}</v-list-item-title>
                <v-list-item-subtitle class="caption">{{
                  s.usn_id
                }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-if="selectedApplicants.length > 5 && !showAllInactive"
              class="px-0"
              @click="showAllInactive = true"
              style="cursor: pointer"
            >
              <v-list-item-content>
                <v-list-item-subtitle class="caption primary--text">
                  + {{ selectedApplicants.length - 5 }} more students (click to
                  view all)
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <v-alert
            color="orange darken-2"
            outlined
            dense
            class="mt-3 mb-3"
            icon="mdi-account-off"
          >
            Students will be marked as <strong>Inactive</strong>. They will no
            longer appear in active student lists.
          </v-alert>

          <v-textarea
            v-model="inactiveRemarks"
            label="Remarks*"
            outlined
            dense
            rows="2"
            hide-details="auto"
            :rules="[rules.required]"
          />
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-btn
            outlined
            @click="(confirmMarkInactive = false), (showAllInactive = false)"
            :disabled="markInactiveLoader"
            >Cancel</v-btn
          >
          <v-spacer />
          <v-btn
            class="orange white--text"
            :loading="markInactiveLoader"
            :disabled="!inactiveRemarks.trim()"
            @click="markSelectedAsInactive"
          >
            <v-icon left small>mdi-account-off</v-icon>
            Confirm Inactive
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { rules } from "@/helpers/rules";
import { studentInvoiceHeaders } from "@/constants/headers";
import examPaperDialog from "@/components/academicPayment/examPaper.vue";
import emailTriggerDialog from "@/components/academicPayment/emailTrigger.vue";
import amountChangeDialog from "@/components/academicPayment/amountChange.vue";
import configureDueDateDialog from "@/components/academicPayment/configureDueDateDialog.vue";

export default {
  components: {
    examPaperDialog,
    emailTriggerDialog,
    amountChangeDialog,
    configureDueDateDialog,
  },
  data() {
    return {
      form: {},
      programs: [],
      partners: [],
      statuses: [
        { id: "Completed", name: "Paid" },
        { id: "Pending", name: "Due" },
      ],
      options_to_display: ["Amount", "Type", "Due Date"],
      rules,
      loading: false,
      batches: [],
      feeRaisedBatches: [],
      studentInvoiceHeaders,
      studentInvoices: [],
      dialog: false,
      studentExamPapers: [],
      sendEmail: false,
      selectedApplicants: [],
      emailTriggerDialog: false,
      payDialog: false,
      applicantData: {},
      feeTypes: [
        { id: "Exam Fee", text: "Exam Fee" },
        { id: "Term Fee", text: "Term Fee" },
        { id: "Annual", text: "Annual Fee" },
        { id: "Re-Admission Fee", text: "Re-Admission Fee" },
        { id: "Convocation Fee", text: "Convocation Fee" },
      ],
      academicPayment: {},
      examPaper: false,
      amountChange: false,
      emailTrigger: false,
      headers: [],
      historicalDownloadLoader: false,
      downloadLoader: false,
      configureDueDateDialog: false,
      confirmMarkInactive: false,
      markInactiveLoader: false,
      inactiveRemarks: "",
      showAllInactive: false,
    };
  },
  watch: {
    form: {
      handler(newVal) {
        this.studentInvoices = [];
      },
      deep: true,
    },
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },

    minDate() {
      return new Date().toISOString().substr(0, 10);
    },

    isDueFilter() {
      return (
        this.form.status === "Pending" &&
        this.form?.types?.length == 1 &&
        this.form?.types[0] == "Exam Fee"
      );
    },
  },
  async created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Fee" }]);
    await this.generateBatchList();
    await this.generateExamBatchList();

    this.init();
  },
  methods: {
    async getStudentInvoices() {
      this.selectedApplicants = [];
      this.loading = true;
      const queryString = this.objectToQueryParams({
        ...this.form,
      });
      try {
        const res = await this.$axios.get(
          `/list_student_invoices_report?${queryString}`,
        );
        this.studentInvoices = res.data.data.sort((a, b) => a.id - b.id);
        if (
          this.form.types &&
          this.form.raised_batch &&
          this.form.status === "Pending"
        ) {
          this.sendEmail = false;
        }

        this.headers = studentInvoiceHeaders
          .filter(
            (h) =>
              h.value !== "papers" || this.form?.types?.includes("Exam Fee"),
          )
          .filter(
            (h) => h.value !== "action" || this.form.status === "Pending",
          );
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
    objectToQueryParams(obj) {
      if (this.currentUser.partner_id != 1) {
        obj.partner_child_id = this.currentUser.partner_id;
      }
      if (obj.year_batch) {
        const [year, batch] = obj.year_batch.split("-");
        obj.year = year;
        obj.batch = batch;
        delete obj.year_batch;
      }
      if (obj.partner_id == 1) {
        delete obj.partner_id;
      }
      if (!obj?.types?.length) delete obj.types;

      return Object.keys(obj)
        .filter(
          (key) =>
            obj[key] !== undefined && obj[key] !== null && obj[key] !== "",
        )
        .map(
          (key) => encodeURIComponent(key) + "=" + encodeURIComponent(obj[key]),
        )
        .join("&");
    },
    async init() {
      this.getStudentInvoices();
      this.getPrograms();
      this.getPartners();
    },
    async getPrograms() {
      let endpoint = "/programs";

      try {
        const response = await this.$axios.get(endpoint);
        this.programs = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async getPartners() {
      try {
        const response = await this.$axios.get("/partners");
        this.partners = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async download() {
      this.downloadLoader = true;
      const queryString = this.objectToQueryParams({
        ...this.form,
      });
      this.$axios
        .get(`/download_student_invoices_report?${queryString}`, {
          responseType: "blob",
        })
        .then((res) => {
          let blob = new Blob([res.data], { type: "text/csv" }),
            url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "Student Invoice.csv");
          document.body.appendChild(link);
          link.click();
          this.downloadLoader = false;
        });
    },

    async historicalFeeDownload() {
      this.historicalDownloadLoader = true;
      this.$axios
        .get(`/download_historical_receipts`, {
          responseType: "blob",
        })
        .then((res) => {
          let blob = new Blob([res.data], { type: "text/csv" }),
            url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "Historical Receipts.csv");
          document.body.appendChild(link);
          link.click();
          this.historicalDownloadLoader = false;
        });
    },
    async generateBatchList() {
      try {
        const response = await this.$axios.get("/get_year_batch");
        this.batches = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async generateExamBatchList() {
      try {
        const response = await this.$axios.get("/fee_raised_batches");
        this.feeRaisedBatches = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async listPapers(item) {
      try {
        const response = await this.$axios.get(
          `/get_student_exam_papers/${item.invoice_id}`,
        );
        // const response = await this.$axios.get(
        //   `/exam_paper/papers_for_invoice/${item.invoice_id}`,
        // );
        this.studentExamPapers = response.data.data;
        this.examPaper = true;
      } catch (error) {
        console.log(error);
      }
    },
    async filterUnPaidData() {
      this.emailTriggerDialog = true;
    },
    triggerPendingApplicantEmail(triggerEmail) {
      this.loading = true;
      const usn_ids = this.selectedApplicants.map((item) => item.usn_id);
      this.$axios
        .post("/send_email_to_pending_invoices", {
          usn_ids: usn_ids,
          email_trigger_data: triggerEmail,
        })
        .then(() => {
          this.loading = false;
          this.emailTrigger = false;
          this.selectedApplicants = [];
          this.$store.commit("showSnackbar", {
            message: "Email triggered successfully",
            color: "success",
          });
        })
        .catch((error) => {
          console.log(error);
        });
    },
    edit(item) {
      this.academicPayment = { ...item };
      this.amountChange = true;
    },

    async showConfigureDueDialog() {
      await this.getStudentInvoices();
      this.configureDueDateDialog = true;
    },

    async configureDueDate(dateRange) {
      this.loading = true;

      try {
        await this.$axios.put("/configure_invoices_due_date", {
          to_date: dateRange.to_date,
          fee_types: this.form.types,
          raised_batch: this.form.raised_batch,
        });

        this.$store.commit("showSnackbar", {
          message: "Due date configured successfully",
          color: "success",
        });

        await this.init();
        this.selectedApplicants = [];
        this.configureDueDateDialog = false;
      } catch (error) {
        console.log(error);
        this.$store.commit("showSnackbar", {
          message:
            error.response?.data?.message || "Failed to configure due date",
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },

    async markSelectedAsInactive() {
      if (!this.inactiveRemarks.trim()) return;

      this.markInactiveLoader = true;
      try {
        const students = this.selectedApplicants.map((item) => ({
          usn_id: item.usn_id,
          student_detail_id: item.student_detail_id,
        }));

        await this.$axios.post("/bulk_mark_students_inactive", {
          students,
          remarks: this.inactiveRemarks.trim(),
        });

        this.$store.commit("showSnackbar", {
          message: `${students.length} student(s) marked as Inactive successfully`,
          color: "success",
        });

        this.confirmMarkInactive = false;
        this.inactiveRemarks = "";
        this.showAllInactive = false;
        this.selectedApplicants = [];
        await this.init();
      } catch (error) {
        console.log(error);
        this.$store.commit("showSnackbar", {
          message:
            error.response?.data?.message ||
            "Failed to mark students as inactive",
          color: "error",
        });
      } finally {
        this.markInactiveLoader = false;
      }
    },
  },
};
</script>

<style scoped>
.v-data-table::v-deep th {
  padding-right: 0px !important;
}
.v-data-table::v-deep td {
  font-size: 13px !important;
  padding-right: 0px !important;
}

/* .v-list-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px;
  transition: 0.3s;
}

.v-list-item:hover {
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
} */

/* ─── Confirm Dialog (shared pattern) ──────── */
.confirm-dialog {
  border-radius: 14px !important;
  overflow: hidden;
}
.confirm-dialog__header {
  padding: 28px 24px 20px;
  text-align: center;
}
.confirm-dialog__header--inactive {
  background: linear-gradient(135deg, #e65100, #fb8c00);
}
.confirm-dialog__title {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
}
.confirm-dialog__sub {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 2px;
}
.confirm-info {
  font-size: 14px;
  color: #424242;
}
.confirm-list {
  background: #f9f9f9 !important;
  border-radius: 8px;
  border: 1px solid #eeeeee;
  max-height: 180px;
  overflow-y: auto;
}
</style>
